import { useEffect } from 'react';
import { log } from 'log';
import { pick } from 'ramda';
import { useDispatch } from 'react-redux';
import { get } from 'js/utils/fetch.util';
import { useAppSelector, useLottoSelector } from 'js/store/hooks';
import { useGetCMSResourceURL } from 'js/hooks/useGetCMSResourceURL';

import * as actions from '../store/actions';
import { useGetInitParams } from './useGetInitParams';

export const useLottoInit = () => {
    const dispatch = useDispatch();
    const initParams = useGetInitParams();
    const getCMSResourceURL = useGetCMSResourceURL();
    const {
        requestStamp,
        lottoRootURL,
        isChangeLangInProgress
    } = useAppSelector(pick([
        'requestStamp',
        'lottoRootURL',
        'isChangeLangInProgress'
    ]));
    const {
        isAppInitError,
        isAppInitialized,
        isAppInitializing,
        isIntegrationScriptFetched,
        isIntegrationScriptFetching
    } = useLottoSelector(pick([
        'isAppInitError',
        'isAppInitialized',
        'isAppInitializing',
        'isIntegrationScriptFetched',
        'isIntegrationScriptFetching'
    ]));

    useEffect(() => {
        if (isIntegrationScriptFetched && !lottoRootURL) {
            log('error', '[LOTTO | Portal] lotto root URL is not configured');

            return;
        }

        if (!isIntegrationScriptFetched || isIntegrationScriptFetching
            || isAppInitialized || isAppInitializing || isAppInitError || isChangeLangInProgress) {
            return;
        }

        (async () => {
            try {
                log('log', '[LOTTO | Portal] app inited with params', initParams);
                dispatch(actions.setAppInitializing());

                if (!window.Lotto?.initializationPromise) {
                    const postInitURL = getCMSResourceURL('/lottoPostInitConf', { t: requestStamp });

                    // we do not await for Promise from .init() because it is stored
                    // in window.Lotto.initializationPromise which is awaited later
                    // this way we do not init twice
                    window.Lotto?.init({
                        ...initParams,
                        postInitConfig: get(postInitURL)
                    });
                }

                // actually await for .init() here
                await window.Lotto?.initializationPromise;

                dispatch(actions.setAppInitialized(true));
            } catch (error) {
                log('log', '[LOTTO | Portal] Lotto init error', error);

                dispatch(actions.setAppInitError());
            }
        })();
    }, [
        dispatch,
        initParams,
        lottoRootURL,
        requestStamp,
        isAppInitError,
        isAppInitialized,
        getCMSResourceURL,
        isAppInitializing,
        isChangeLangInProgress,
        isIntegrationScriptFetched,
        isIntegrationScriptFetching
    ]);
};
